import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
  EntityHistoryTimelineEntryDto,
  EntityHistoryTimelineResponseDto,
  GroundfloorManagementAPIs,
  KnownSortOrder
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { RestError } from '@azure/core-http';
import { useApi } from '../api/api.context';
import { QueryKeys } from './query-keys.constants';
import { EntityHistoryEventGroup } from '../../ui/components/pure/entity-history-timeline/entity-history-timeline.types';
import {
  deduplicateEntries,
  groupEntriesByCorrelation
} from '../../ui/components/pure/entity-history-timeline/entity-history-timeline.utils';

const PAGE_SIZE = 20;

interface TimelineQueryParams {
  entityId: string;
  fromDate?: Date;
  toDate?: Date;
  limit?: number;
  offset?: number;
  sortOrder?: KnownSortOrder;
}

/**
 * Fetches one page of an entity's timeline. Each entity type has its own controller so the permissions
 * check is scoped to that type, so the timeline hook is given the one it should call.
 */
type TimelineFetcher = (
  api: GroundfloorManagementAPIs,
  params: TimelineQueryParams
) => Promise<EntityHistoryTimelineResponseDto>;

function toTimelineOptions(params: TimelineQueryParams) {
  return {
    fromDate: params.fromDate,
    toDate: params.toDate,
    limit: params.limit,
    offset: params.offset,
    sortOrder: params.sortOrder
  };
}

const fetchTeamMemberTimeline: TimelineFetcher = (api, params) =>
  api.teamMembersEntityHistoryTimelineController.getTimeline(params.entityId, toTimelineOptions(params));

const fetchCourierTimeline: TimelineFetcher = (api, params) =>
  api.couriersEntityHistoryTimelineController.getTimeline(params.entityId, toTimelineOptions(params));

function useEntityHistoryQuery(
  entityType: string,
  key: string,
  fetchTimeline: TimelineFetcher,
  params: TimelineQueryParams,
  enabled: boolean
) {
  const api = useApi();

  return useQuery<EntityHistoryTimelineResponseDto, RestError>(
    [
      QueryKeys.ENTITY_HISTORY,
      entityType,
      key,
      params.entityId,
      params.toDate,
      params.fromDate,
      params.offset,
      params.sortOrder
    ],
    () => fetchTimeline(api, params),
    { enabled, keepPreviousData: true }
  );
}

/**
 * Custom hook to manage entity history timeline data fetching and state.
 * Handles pagination for older and newer entries, pivot date changes, and grouping of events.
 *
 * The hook maintains separate state for older and newer entries to allow independent pagination in both directions.
 * It also tracks whether the newer query has been enabled and whether it has returned results to provide user feedback.
 */
function useEntityHistoryTimeline(entityType: string, fetchTimeline: TimelineFetcher, entityId: string) {
  const queryClient = useQueryClient();
  const [pivotDate, setPivotDate] = useState<Date>(() => new Date());
  const [olderOffset, setOlderOffset] = useState(0);
  const [newerEnabled, setNewerEnabled] = useState(false);
  const [newerOffset, setNewerOffset] = useState(0);
  const [olderAccumulated, setOlderAccumulated] = useState<EntityHistoryTimelineEntryDto[]>([]);
  const [newerAccumulated, setNewerAccumulated] = useState<EntityHistoryTimelineEntryDto[]>([]);
  const [noNewerEventsFound, setNoNewerEventsFound] = useState(false);

  const enabled = !!entityId;

  const olderQuery = useEntityHistoryQuery(
    entityType,
    'older',
    fetchTimeline,
    { entityId, toDate: pivotDate, limit: PAGE_SIZE, offset: olderOffset },
    enabled
  );

  const newerQuery = useEntityHistoryQuery(
    entityType,
    'newer',
    fetchTimeline,
    { entityId, fromDate: pivotDate, limit: PAGE_SIZE, offset: newerOffset, sortOrder: KnownSortOrder.Asc },
    enabled && newerEnabled
  );

  // Track whether first newer fetch has been handled
  const firstNewerHandled = useRef(false);

  // Show snackbar if first newer fetch returns no results
  useEffect(() => {
    if (newerEnabled && newerQuery.isSuccess && !firstNewerHandled.current) {
      firstNewerHandled.current = true;
      if (newerQuery.data.entries.length === 0) {
        setNoNewerEventsFound(true);
      }
    }
  }, [newerEnabled, newerQuery.isSuccess, newerQuery.data]);

  // Accumulate older results when they arrive
  const olderEntries = useMemo(() => {
    if (!olderQuery.data) return olderAccumulated;
    const combined = [...olderAccumulated, ...olderQuery.data.entries];
    return deduplicateEntries(combined);
  }, [olderQuery.data, olderAccumulated]);

  // Accumulate newer results when they arrive
  const newerEntries = useMemo(() => {
    if (!newerQuery.data) return newerAccumulated;
    const combined = [...newerAccumulated, ...newerQuery.data.entries];
    return deduplicateEntries(combined);
  }, [newerQuery.data, newerAccumulated]);

  // Merge and group all entries
  const groups: EntityHistoryEventGroup[] = useMemo(() => {
    // Newer entries are fetched in ascending order but must be reversed to match the descending render order.
    // .slice() creates a shallow copy so .reverse() doesn't mutate the newerEntries array in place.
    const allEntries = deduplicateEntries([...newerEntries.slice().reverse(), ...olderEntries]);
    return groupEntriesByCorrelation(allEntries);
  }, [olderEntries, newerEntries]);

  const handleSetPivotDate = useCallback((date: Date) => {
    setPivotDate(date);
    setOlderOffset(0);
    setNewerEnabled(false);
    setNewerOffset(0);
    setOlderAccumulated([]);
    setNewerAccumulated([]);
    setNoNewerEventsFound(false);
    firstNewerHandled.current = false;
  }, []);

  const loadOlder = useCallback(() => {
    if (olderQuery.data) {
      setOlderAccumulated((prev) => deduplicateEntries([...prev, ...olderQuery.data!.entries]));
      setOlderOffset((prev) => prev + PAGE_SIZE);
    }
  }, [olderQuery.data]);

  const loadNewer = useCallback(async () => {
    setNoNewerEventsFound(false);

    if (!newerEnabled) {
      // First click: enable the newer query at offset 0
      setNewerEnabled(true);
      return;
    }

    const currentEntries = newerQuery.data?.entries ?? [];

    if (currentEntries.length === PAGE_SIZE) {
      // Full page — accumulate and advance offset
      setNewerAccumulated((prev) => deduplicateEntries([...prev, ...currentEntries]));
      setNewerOffset((prev) => prev + PAGE_SIZE);
    } else {
      // Partial or empty page — refetch same offset for fresh data
      const previousCount = deduplicateEntries([...newerAccumulated, ...currentEntries]).length;

      await queryClient.invalidateQueries([QueryKeys.ENTITY_HISTORY, entityType, 'newer', entityId]);

      const freshData = queryClient.getQueryData<EntityHistoryTimelineResponseDto>([
        QueryKeys.ENTITY_HISTORY,
        entityType,
        'newer',
        entityId,
        undefined,
        pivotDate,
        newerOffset,
        KnownSortOrder.Asc
      ]);
      const freshEntries = freshData?.entries ?? currentEntries;
      const newCount = deduplicateEntries([...newerAccumulated, ...freshEntries]).length;

      if (newCount <= previousCount) {
        setNoNewerEventsFound(true);
      }
    }
  }, [newerEnabled, newerQuery.data, newerAccumulated, queryClient, entityType, entityId, pivotDate, newerOffset]);

  const clearNoNewerEvents = useCallback(() => {
    setNoNewerEventsFound(false);
  }, []);

  return {
    groups,
    isLoading: olderQuery.isLoading,
    isError: olderQuery.isError || newerQuery.isError,
    pivotDate,
    setPivotDate: handleSetPivotDate,
    loadOlder,
    loadNewer,
    hasOlderEntries: olderQuery.data?.hasMoreResults ?? false,
    isLoadingOlder: olderQuery.isFetching,
    isLoadingNewer: newerQuery.isFetching,
    noNewerEventsFound,
    clearNoNewerEvents
  };
}

export function useTeamMemberEntityHistoryTimeline(teamMemberID: string) {
  return useEntityHistoryTimeline('team-member', fetchTeamMemberTimeline, teamMemberID);
}

export function useCourierEntityHistoryTimeline(courierID: string) {
  return useEntityHistoryTimeline('courier', fetchCourierTimeline, courierID);
}
